import {
  Box,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  Typography,
} from '@mui/material';
import { Controller, useFormContext } from 'react-hook-form';
import { useCallback, useMemo } from 'react';
import { StyledFormSection } from './_elements';
import { Stack as FracStack } from '@src/types/Stacks';
import { SiteMeta } from '@src/types/SiteMeta';
import storeConnector from '@src/store/storeConnector';
import { VALIDATION } from '../constants';
import { hasErrorCode, getErrorMessage } from '../utils';
import CommandSelect from './CommandSelect';

interface FormValues {
  stackId: string | null;
  startCommands: string[];
  stopCommands: string[];
}

interface CommandsStackSectionProps {
  stacks: FracStack[];
  siteMeta: SiteMeta;
}

const CommandsStackSection: React.FC<CommandsStackSectionProps> = ({
  stacks,
  siteMeta,
}) => {
  const {
    control,
    formState: { errors },
    trigger,
  } = useFormContext<FormValues>();

  const hasValidationError = useMemo(
    () =>
      hasErrorCode(errors.stackId, VALIDATION.COMMANDS_OR_STACK_REQUIRED.code),
    [errors.stackId],
  );

  const sortedStacks = useMemo(
    () => stacks?.slice().sort((a, b) => a.name.localeCompare(b.name)),
    [stacks],
  );

  const createStackIdChangeHandler = useCallback(
    (onChange: (value: string | null) => void) =>
      (event: SelectChangeEvent<string>) => {
        const value = event.target.value === '' ? null : event.target.value;
        onChange(value);

        if (hasValidationError) {
          trigger('stackId');
        }
      },
    [hasValidationError, trigger],
  );

  return (
    <StyledFormSection>
      <Typography variant='h6' sx={{ mt: 2 }}>
        Commands
      </Typography>
      <Stack spacing={2} sx={{ mt: 2 }} direction={'row'}>
        <CommandSelect
          name='startCommands'
          label='Beginning Commands'
          commands={siteMeta?.ui.EventCommands}
        />
        <CommandSelect
          name='stopCommands'
          label='Ending Commands'
          commands={siteMeta?.ui.EventCommands}
        />
      </Stack>
      <Controller
        name='stackId'
        control={control}
        render={({ field }) => (
          <FormControl fullWidth sx={{ mt: 4 }} error={hasValidationError}>
            <InputLabel id='select-stack' shrink>
              Stack ID (Optional)
            </InputLabel>
            <Select
              {...field}
              value={field.value || ''}
              onChange={createStackIdChangeHandler(field.onChange)}
              error={hasValidationError || !!errors.stackId}
              labelId='select-stack'
              label='Stack ID (Optional)'
              displayEmpty
            >
              <MenuItem value=''>
                <Box
                  component='em'
                  sx={{ color: 'text.disabled', opacity: 0.4 }}
                >
                  None
                </Box>
              </MenuItem>
              {sortedStacks?.map((stack) => (
                <MenuItem key={stack.id} value={stack.id}>
                  {stack.name}
                </MenuItem>
              ))}
            </Select>
            {hasValidationError && (
              <FormHelperText error>
                {getErrorMessage(errors.stackId)}
              </FormHelperText>
            )}
          </FormControl>
        )}
      />
    </StyledFormSection>
  );
};

export default storeConnector(CommandsStackSection, {
  config: ['siteMeta'],
});
